import "./EngagementLetterSettings.scss";
import { Button, Loader, Modal, Text, TextInput } from "@mantine/core";
import { IconEye, IconInfoCircle } from "@tabler/icons-react";
import PublishConfirmation from "./PublishConfirmation/PublishConfirmation";
import LetterPreview from "./LetterPreview/LetterPreview";
import TextEditor from "@/components/TextEditor/TextEditor";
import AttachmentManager from "./AttachmentManager/AttachmentManager";
import { useEngagementLetterStore } from "@/utils/stores/engagementLetterStore";
import { useEffect } from "react";

const EngagementLetterSettings = () => {
  const {
    engagementLetterSettings,
    isPreviewModalOpen,
    isPublishModalOpen,
    openPreviewModal,
    closePreviewModal,
    openPublishModal,
    closePublishModal,
    updateSettings,
    saveTemplate,
    fetchTemplate,
  } = useEngagementLetterStore();

  // Fetch template on mount
  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  const handleEngagementLetterChange = (newSettings: {
    title: string;
    content: string;
  }) => {
    updateSettings(newSettings.title, newSettings.content);
  };

  const isPublishing = engagementLetterSettings.loading;

  return (
    <>
      {/* Main Button Section */}
      <div className="letter-button-container">
        <Button
          onClick={openPreviewModal}
          variant="transparent"
          leftSection={<IconEye />}
        >
          Preview
        </Button>
        <Button
          size="md"
          color="#0077CC"
          radius="0.5rem"
          onClick={openPublishModal}
        >
          Save
        </Button>
      </div>

      {/* Preview Modal */}
      <Modal
        opened={isPreviewModalOpen}
        onClose={closePreviewModal}
        title={
          <div className="preview-header">
            <IconEye color="#0077CC" />
            <Text size="xl" fw={500} c={"#0077CC"}>
              Previewing Engagement Letter
            </Text>
          </div>
        }
        fullScreen
      >
        <LetterPreview
          content={{
            title:
              engagementLetterSettings.title ??
              "Engagement Letter for Tax Preparation Services",
            content: engagementLetterSettings.content,
            header: "",
          }}
        />
      </Modal>

      {/* Publish Modal */}
      <Modal
        opened={isPublishModalOpen}
        onClose={closePublishModal}
        radius="md"
        size="lg"
        title={
          <Text size="xl" fw={500}>
            Publish new changes?
          </Text>
        }
      >
        {isPublishing ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Loader size="lg" />
            <Text m="md" fw={500}>
              Publishing Changes
            </Text>
          </div>
        ) : (
          <PublishConfirmation
            closeModal={closePublishModal}
            onPublish={saveTemplate}
          />
        )}
      </Modal>

      {/* Header and Content Section */}
      <div className="sections-container">
        <div className="section">
          {/* Header Section */}
          <div className="sub-section">
            <div className="section-header">
              <Text mt="lg" fw={500}>
                Header*
              </Text>
              <Text size="sm" mb="lg" c="dimmed">
                Add a clear title that reflects the nature of this engagement
                letter
              </Text>
              <TextInput
                placeholder="Title"
                size="md"
                radius="md"
                value={engagementLetterSettings.title}
                onChange={(event) =>
                  handleEngagementLetterChange({
                    ...engagementLetterSettings,
                    title: event.target.value,
                  })
                }
              />
            </div>
          </div>

          {/* Content Section */}
          <div className="sub-section">
            <div className="section-header">
              <Text mt="lg" fw={500}>
                Content*
              </Text>
              <Text size="sm" mb="lg" c="dimmed">
                Outline your tax preparation services, taxpayer
                responsibilities, and other terms of service
              </Text>
              <div className="engagement-letter-disclaimer">
                <IconInfoCircle />
                <Text fw={500}>
                  Disclaimer: SmartWiz provides tools for drafting and sharing
                  engagement letters. Users are solely responsible for their
                  content and compliance.
                </Text>
              </div>
              <TextEditor
                content={engagementLetterSettings.content}
                onChange={(newContent) =>
                  handleEngagementLetterChange({
                    ...engagementLetterSettings,
                    content: newContent,
                  })
                }
              />
            </div>
          </div>

          {/* Attachments Section */}
          <div className="sub-section">
            <AttachmentManager />
          </div>
        </div>
      </div>
    </>
  );
};

export default EngagementLetterSettings;
